import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import './Services.css';
import { services } from '../utils/mockData';

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="container">
          <div className="services-hero-content">
            <h1>Strategic Advisory Services</h1>
            <p className="services-hero-subtitle">
              Tailored engagements for leaders navigating complexity at the highest levels. 
              Evidence-based strategy, systems thinking, and global perspective for challenges 
              that demand more than conventional consulting.
            </p>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="services-overview section">
        <div className="container">
          <div className="section-header">
            <h2>Engagement Areas</h2>
            <p className="section-subtitle">
              Select the area that aligns with your strategic challenge
            </p>
          </div>
          
          <div className="services-list">
            {services.map((service) => (
              <div key={service.id} className="service-item">
                <div className="service-main">
                  <div className="service-header">
                    <h3>{service.title}</h3>
                    <p className="service-subtitle">{service.subtitle}</p>
                  </div>
                  <p className="service-description">{service.description}</p>
                  
                  <button
                    className="btn btn-secondary"
                    onClick={() => setSelectedService(service.id === selectedService ? null : service.id)}
                  >
                    {selectedService === service.id ? 'Show Less' : 'Learn More'}
                    <ArrowRight size={18} />
                  </button>
                </div>
                
                {selectedService === service.id && (
                  <div className="service-details">
                    <div className="service-detail-section">
                      <h4>Challenges Addressed</h4>
                      <ul className="service-list">
                        {service.challenges.map((challenge, index) => (
                          <li key={index}>
                            <CheckCircle size={18} />
                            <span>{challenge}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="service-detail-section">
                      <h4>Strategic Outcomes</h4>
                      <ul className="service-list">
                        {service.outcomes.map((outcome, index) => (
                          <li key={index}>
                            <CheckCircle size={18} />
                            <span>{outcome}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="service-ideal">
                      <span className="ideal-label">Ideal for:</span>
                      <span className="ideal-value">{service.idealFor}</span>
                    </div>
                    
                    <Link to="/contact" className="btn btn-primary">
                      Discuss This Engagement
                      <ArrowRight size={18} />
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How I Work Section */}
      <section className="how-work-section section-light">
        <div className="container">
          <div className="section-header">
            <h2>How Strategic Engagements Work</h2>
            <p className="section-subtitle">
              A collaborative process designed for depth, not speed
            </p>
          </div>
          <div className="work-process">
            <div className="process-step">
              <div className="step-number">01</div>
              <h3>Discovery & Diagnosis</h3>
              <p>
                Initial consultations to understand the challenge in full context—not just symptoms, 
                but underlying systems and constraints. This phase establishes clarity on what we're 
                actually solving.
              </p>
            </div>
            <div className="process-step">
              <div className="step-number">02</div>
              <h3>Systems Mapping & Analysis</h3>
              <p>
                Deep analysis of interdependencies, stakeholder dynamics, and constraint identification. 
                Using evidence-based frameworks to reveal patterns and leverage points invisible to 
                conventional analysis.
              </p>
            </div>
            <div className="process-step">
              <div className="step-number">03</div>
              <h3>Strategy Development</h3>
              <p>
                Co-creating strategic interventions that address root causes and create cascading 
                positive effects. Strategies are grounded in empirical evidence and tailored to your 
                specific organizational and institutional context.
              </p>
            </div>
            <div className="process-step">
              <div className="step-number">04</div>
              <h3>Implementation Guidance</h3>
              <p>
                Ongoing advisory support during execution, helping navigate complexity as it unfolds. 
                This isn't about rigid plans—it's about adaptive strategy informed by systems understanding.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Engagement Models Section */}
      <section className="engagement-models-section section">
        <div className="container">
          <div className="section-header">
            <h2>Engagement Models</h2>
            <p className="section-subtitle">
              Flexible approaches tailored to your needs
            </p>
          </div>
          <div className="models-grid">
            <div className="model-card">
              <h3>Strategic Session</h3>
              <div className="model-duration">Half-day or Full-day</div>
              <p>
                Intensive focused sessions for leaders seeking strategic clarity on specific challenges. 
                Ideal for initial diagnosis or when you need an external perspective to break through 
                internal deadlock.
              </p>
              <div className="model-includes">
                <span className="includes-label">Includes:</span>
                <ul>
                  <li>Pre-session context review</li>
                  <li>Structured diagnostic process</li>
                  <li>Strategic framework development</li>
                  <li>Action-oriented recommendations</li>
                </ul>
              </div>
            </div>
            
            <div className="model-card model-featured">
              <div className="featured-badge">Most Common</div>
              <h3>Advisory Retainer</h3>
              <div className="model-duration">3-12 months</div>
              <p>
                Ongoing strategic partnership for organizations navigating extended transformation or 
                facing evolving complex challenges. Regular touchpoints ensure strategy adapts as 
                circumstances evolve.
              </p>
              <div className="model-includes">
                <span className="includes-label">Includes:</span>
                <ul>
                  <li>Monthly strategic sessions</li>
                  <li>Continuous access for urgent decisions</li>
                  <li>Systems-level progress monitoring</li>
                  <li>Board-ready strategic documentation</li>
                </ul>
              </div>
            </div>
            
            <div className="model-card">
              <h3>Project-Based Engagement</h3>
              <div className="model-duration">Defined scope & timeline</div>
              <p>
                Structured engagements for specific strategic initiatives—market entry strategy, 
                policy design, institutional transformation, or venture strategy. Clear deliverables 
                with flexibility for emergent insights.
              </p>
              <div className="model-includes">
                <span className="includes-label">Includes:</span>
                <ul>
                  <li>Comprehensive situation analysis</li>
                  <li>Strategic framework development</li>
                  <li>Implementation roadmap</li>
                  <li>Stakeholder presentation support</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes This Different Section */}
      <section className="different-section section-dark">
        <div className="container">
          <div className="different-content">
            <h2>What Makes This Advisory Different</h2>
            <div className="different-grid">
              <div className="different-item">
                <h4>No Cookie-Cutter Frameworks</h4>
                <p>
                  Every engagement is built from first principles, tailored to your specific context. 
                  Standard consulting frameworks are starting points, not solutions.
                </p>
              </div>
              <div className="different-item">
                <h4>Evidence Over Opinion</h4>
                <p>
                  Recommendations grounded in decades of peer-reviewed research across multiple 
                  contexts, not just experience or intuition.
                </p>
              </div>
              <div className="different-item">
                <h4>Systems Thinking, Not Symptom Treatment</h4>
                <p>
                  Focus on root causes and interdependencies rather than quick fixes that create 
                  unintended consequences elsewhere in the system.
                </p>
              </div>
              <div className="different-item">
                <h4>Global Perspective, Local Insight</h4>
                <p>
                  Cross-cultural experience across four continents brings patterns and solutions 
                  that transcend local conventional wisdom.
                </p>
              </div>
              <div className="different-item">
                <h4>Academic Rigor, Practical Impact</h4>
                <p>
                  The rare combination of world-class research credentials with hands-on advisory 
                  experience in government, enterprise, and institutional contexts.
                </p>
              </div>
              <div className="different-item">
                <h4>Intellectual Honesty</h4>
                <p>
                  If your challenge is outside my domain of expertise, I'll tell you. No overselling, 
                  no false certainty—just honest assessment of how I can help.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta-section section">
        <div className="container">
          <div className="services-cta-content">
            <h2>Let's Discuss Your Strategic Challenge</h2>
            <p>
              Book an initial strategy session to explore whether my approach aligns with your needs. 
              These conversations are exploratory—not sales pitches—and you'll leave with valuable 
              perspective regardless of whether we work together.
            </p>
            <Link to="/contact" className="btn btn-primary btn-large">
              Book Strategy Session
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;